/**
 * AURA Intelligence Feed — Streaming Sense mode
 *
 * Streams the intelligence brief token-by-token from Claude.
 * Skeleton animations while loading, typewriter reveal as text arrives.
 * Cluster cards stagger in as each brief completes.
 */

import { useAuraStore } from '../state/store';

const GATEWAY = import.meta.env.VITE_API_URL || '';

// ── CSS ───────────────────────────────────────────────────────────────────────

const SENSE_CSS = `
  #sense-feed {
    position: fixed;
    /* Full-screen — Sense mode IS the brief. No split, no competition. */
    inset: 0;
    background: #06060d; /* fully opaque — nothing showing through */
    border-top: none;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    z-index: 30;
    opacity: 0;
    transition: opacity 0.5s ease;
    display: flex;
    flex-direction: column;
    pointer-events: none;
  }
  #sense-feed.visible {
    opacity: 1;
    pointer-events: auto;
  }
  .sf-inner {
    display: flex;
    flex: 1;
    overflow: hidden;
    /* Generous margins. Bottom 100px clears the fixed mode switcher. */
    padding: 48px 10% 100px 10%;
    gap: 8%;
    max-width: 1200px;
    margin: 0 auto;
    width: 100%;
    box-sizing: border-box;
  }
  .sf-left {
    flex: 5;
    display: flex;
    flex-direction: column;
    gap: 0; /* gaps controlled per element */
    overflow: hidden;
    min-width: 0;
  }
  .sf-right {
    flex: 3;
    display: flex;
    flex-direction: column;
    gap: 20px;
    overflow-y: auto;
    scrollbar-width: none;
    border-left: 1px solid rgba(255,255,255,0.06);
    padding-left: 8%;
  }
  .sf-right::-webkit-scrollbar { display: none; }
  /* Divider replaced by border-left on sf-right */
  .sf-divider { display: none; }

  /* ── Skeleton shimmer ─────────────────────────────────── */
  @keyframes sf-shimmer {
    0% { background-position: -300% 0; }
    100% { background-position: 300% 0; }
  }
  .sf-skeleton {
    background: linear-gradient(
      90deg,
      rgba(255,255,255,0.03) 0%,
      rgba(255,255,255,0.07) 40%,
      rgba(255,255,255,0.03) 80%
    );
    background-size: 300% 100%;
    animation: sf-shimmer 2s ease-in-out infinite;
    border-radius: 3px;
  }
  .sf-skeleton-line {
    height: 16px;
    margin-bottom: 10px;
    border-radius: 3px;
  }
  .sf-skeleton-card {
    padding: 12px 14px;
    border: 1px solid rgba(255,255,255,0.05);
    border-radius: 4px;
    margin-bottom: 8px;
  }
  .sf-skeleton-card-title {
    height: 11px;
    width: 60%;
    margin-bottom: 8px;
    border-radius: 2px;
  }
  .sf-skeleton-card-text {
    height: 10px;
    width: 90%;
    margin-bottom: 4px;
    border-radius: 2px;
  }
  .sf-skeleton-card-text-short {
    height: 10px;
    width: 70%;
    border-radius: 2px;
  }

  /* ── Eyebrow + attribution — full page, given room to breathe */
  .sf-eyebrow {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.20em;
    text-transform: uppercase;
    color: rgba(255,255,255,0.42);
    margin-bottom: 6px;
  }
  .sf-attribution {
    font-size: 10px;
    font-weight: 400;
    color: rgba(255,255,255,0.30);
    letter-spacing: 0.04em;
    margin-bottom: 28px;
    font-variant-numeric: tabular-nums;
  }

  /* ── Brief text — full screen: lead editorial type ─────── */
  .sf-brief {
    font-size: 20px;
    font-weight: 400;
    color: rgba(255,255,255,0.88);
    line-height: 1.65;
    letter-spacing: -0.01em;
    flex: 1; /* fills space, pushes recommendation down */
    overflow-y: auto;
    scrollbar-width: none;
    max-width: 640px;
    margin-bottom: 0;
    padding-top: 4px; /* visual breathing room after attribution */
  }
  .sf-brief::-webkit-scrollbar { display: none; }
  /* Blinking cursor during streaming */
  .sf-cursor {
    display: inline-block;
    width: 2px;
    height: 1em;
    background: rgba(255,255,255,0.5);
    margin-left: 2px;
    vertical-align: text-bottom;
    animation: sf-blink 0.7s step-end infinite;
  }
  @keyframes sf-blink { 0%,100%{opacity:1} 50%{opacity:0} }

  .sf-rule { display: none; }

  /* ── Recommendation — anchored to bottom of left column ─ */
  .sf-rec {
    border-top: 1px solid rgba(255,255,255,0.06);
    padding-top: 18px;
    margin-top: auto; /* pushes rec to bottom of the column */
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  .sf-rec-label {
    font-size: 8px;
    font-weight: 700;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: rgba(79,209,160,0.80); /* raised from 0.55 — own the teal */
    margin-bottom: 4px;
  }
  .sf-rec-text {
    font-size: 14px;
    color: rgba(255,255,255,0.72);
    line-height: 1.58;
    font-weight: 400;
    max-width: 560px;
  }
  /* finding 10: CTA full-width, below rec text, read→decide→act */
  .sf-rec-cta {
    width: 100%;
    padding: 10px 0;
    background: rgba(255,255,255,0.08);
    border: 1px solid rgba(255,255,255,0.14);
    border-radius: 4px;
    color: rgba(255,255,255,0.82);
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 0.06em;
    cursor: pointer;
    font-family: 'Inter', sans-serif;
    text-align: center;
    transition: background 0.2s ease, border-color 0.2s ease;
  }
  .sf-rec-cta:hover { background: rgba(255,255,255,0.14); border-color: rgba(255,255,255,0.24); }

  /* ── Cluster cards — finding 6: whitespace divides, not lines ─ */
  .sf-cluster-card {
    padding: 8px 0; /* removed horizontal padding — left-flush like annotations */
    cursor: pointer;
    /* No border-bottom — space separates, lines add weight without information */
    transition: opacity 0.4s ease, transform 0.4s ease;
    opacity: 0;
    transform: translateY(6px);
  }
  .sf-cluster-card.revealed { opacity: 1; transform: translateY(0); }
  .sf-cluster-card:hover .sf-cluster-name { color: rgba(255,255,255,0.88); }
  .sf-cluster-header {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    margin-bottom: 5px;
    gap: 8px;
  }
  .sf-cluster-name {
    font-size: 12px;
    font-weight: 600;
    color: rgba(255,255,255,0.70);
    letter-spacing: 0.01em;
    flex: 1;
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    transition: color 0.2s ease;
  }
  .sf-cluster-data {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
  }
  .sf-cluster-signals {
    font-size: 9px;
    color: rgba(255,255,255,0.22);
    letter-spacing: 0.04em;
    font-variant-numeric: tabular-nums;
  }
  .sf-cluster-trend {
    font-size: 10px;
    font-weight: 700;
    line-height: 1;
  }
  .sf-cluster-trend.rising { color: #4fd1a0; }
  .sf-cluster-trend.stable { color: rgba(255,255,255,0.22); }
  .sf-cluster-trend.fading { color: #d14f4f; }
  .sf-cluster-brief {
    font-size: 11px;
    color: rgba(255,255,255,0.50);
    line-height: 1.55;
    min-height: 14px;
    margin-top: 3px;
  }
  .sf-cluster-cursor {
    display: inline-block;
    width: 1px;
    height: 0.85em;
    background: rgba(255,255,255,0.25);
    margin-left: 1px;
    vertical-align: text-bottom;
    animation: sf-blink 0.7s step-end infinite;
  }

  /* ── Bottom strip ─────────────────────────────────────── */
  .sf-bottom {
    padding: 10px 10% 18px; /* bottom padding within the strip itself */
    border-top: 1px solid rgba(255,255,255,0.05);
    display: flex;
    align-items: center;
    justify-content: space-between;
    max-width: 1200px;
    margin: 0 auto;
    width: 100%;
    box-sizing: border-box;
  }
  .sf-timestamp { font-size: 10px; color: rgba(255,255,255,0.25); letter-spacing: 0.06em; }
  /* finding 8: interactive element must look interactive at rest */
  .sf-refresh {
    font-size: 9px; color: rgba(255,255,255,0.35); cursor: pointer;
    letter-spacing: 0.08em; text-transform: uppercase; user-select: none;
  }
  .sf-refresh:hover { color: rgba(255,255,255,0.58); text-decoration: underline; text-underline-offset: 2px; }
  .sf-refresh.spinning { animation: sf-spin 1s linear infinite; }
  @keyframes sf-spin { from{transform:rotate(0)} to{transform:rotate(360deg)} }
`;

// ── Streaming parser ──────────────────────────────────────────────────────────

function parseSSEChunk(chunk: string): Array<{ type: string; [k: string]: any }> {
  const events: Array<{ type: string; [k: string]: any }> = [];
  const lines = chunk.split('\n');
  for (const line of lines) {
    if (line.startsWith('data: ')) {
      try {
        const data = JSON.parse(line.slice(6));
        events.push(data);
      } catch { /* partial chunk, skip */ }
    }
  }
  return events;
}

// ── Component ─────────────────────────────────────────────────────────────────

export class SenseMode {
  private panel: HTMLElement | null = null;
  private visible = false;
  private streaming = false;
  private lastFetch = 0;
  private CACHE_MS = 15 * 60 * 1000; // 15 min cache

  // Accumulated state from stream
  private briefText = '';
  private recText = '';
  private clusters = new Map<string, { topic: string; trend: string; brief: string; signalCount: number }>();
  private generatedAt = 0;

  constructor() {
    const style = document.createElement('style');
    style.textContent = SENSE_CSS;
    document.head.appendChild(style);

    this.panel = document.createElement('div');
    this.panel.id = 'sense-feed';
    document.body.appendChild(this.panel);

    this.renderEmpty();
  }

  // ── Visibility ────────────────────────────────────────────────────────────

  show() {
    if (this.visible) return;
    this.visible = true;
    this.panel?.classList.add('visible');
    this.dimField(true);
    document.getElementById('query-hint')?.classList.remove('visible');

    if (Date.now() - this.lastFetch > this.CACHE_MS) {
      this.startStream();
    } else {
      this.renderFull();
    }
  }

  hide() {
    if (!this.visible) return;
    this.visible = false;
    this.panel?.classList.remove('visible');
    this.dimField(false);
    document.getElementById('query-hint')?.classList.add('visible');
  }

  private dimField(_dim: boolean) {
    // No-op. Mode switcher stays put.
    // Content uses padding-bottom:100px to stay above it.
  }

  // ── Streaming ─────────────────────────────────────────────────────────────

  private async startStream() {
    if (this.streaming || !GATEWAY) return;
    this.streaming = true;

    // Reset state
    this.briefText = '';
    this.recText = '';
    this.clusters.clear();
    this.generatedAt = 0;

    this.renderSkeleton();

    // ── Try cached endpoint first (instant) ──────────────────────────────
    // Falls back to streaming only if cache is empty (first cold start)
    try {
      const cachedRes = await fetch(`${GATEWAY}/api/ai/sense-brief/cached`, {
        signal: AbortSignal.timeout(35_000), // wait up to 35s on cold start
      });

      if (cachedRes.ok) {
        const data = await cachedRes.json() as any;
        this.briefText = data.brief || '';
        this.recText = data.recommendation || '';
        this.generatedAt = data.generatedAt || Date.now();
        this.clusters.clear();
        (data.clusters || []).forEach((cl: any) => {
          this.clusters.set(cl.topic, {
            topic: cl.topic, brief: cl.brief, trend: cl.trend,
            signalCount: cl.signalCount,
          });
        });
        this.lastFetch = Date.now();
        this.streaming = false;
        this.renderFull();
        return;
      }
    } catch (err: any) {
      console.warn('[SenseMode] cached endpoint failed:', err.message);
      // Fall through to streaming
    }

    // ── Streaming fallback ────────────────────────────────────────────────

    try {
      const res = await fetch(`${GATEWAY}/api/ai/sense-brief/stream`, {
        signal: AbortSignal.timeout(25_000), // 25s hard cutoff
        headers: { 'Accept': 'text/event-stream' },
      });

      if (!res.ok || !res.body) throw new Error(`stream failed (${res.status})`);

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let briefStarted = false;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const events = parseSSEChunk(buffer);
        // Clear processed lines from buffer
        const lastNewline = buffer.lastIndexOf('\n');
        if (lastNewline > -1) buffer = buffer.slice(lastNewline + 1);

        for (const event of events) {
          if (!this.visible) { reader.cancel(); return; }

          switch (event.type) {
            case 'meta':
              // Field stats available — update skeleton with real counts
              this.updateSkeletonMeta(event.clusterCount, event.signalCount);
              break;

            case 'brief_token':
              if (!briefStarted) {
                briefStarted = true;
                this.clearSkeleton();
              }
              this.briefText += event.text;
              this.updateBriefEl();
              break;

            case 'cluster_start':
              this.clusters.set(event.topic, {
                topic: event.topic, trend: event.trend,
                brief: '', signalCount: event.signalCount,
              });
              this.addOrUpdateClusterCard(event.topic);
              break;

            case 'cluster_token':
              if (this.clusters.has(event.topic)) {
                this.clusters.get(event.topic)!.brief += event.text;
                this.updateClusterCard(event.topic);
              }
              break;

            case 'rec_token':
              this.recText += event.text;
              this.updateRecEl();
              break;

            case 'done':
              this.generatedAt = event.generatedAt || Date.now();
              this.lastFetch = Date.now();
              this.finalize();
              break;

            case 'error':
              this.briefText = 'Intelligence brief generation encountered an error. The field is still live.';
              this.updateBriefEl(true);
              break;
          }
        }
      }
    } catch (err: any) {
      console.warn('[SenseMode] stream error:', err.message);
      if (this.briefText === '') {
        this.briefText = 'Brief is generating. Check back in a moment — the field is processing signals.';
      }
      this.renderFull();
    } finally {
      this.streaming = false;
    }
  }

  // ── Rendering helpers (incremental) ──────────────────────────────────────

  private renderEmpty() {
    if (!this.panel) return;
    this.panel.innerHTML = `
      <div class="sf-inner">
        <div class="sf-left">
          <div>
            <div class="sf-eyebrow">Intelligence brief</div>
            <div class="sf-attribution" id="sf-attribution">Soho + Artists & Robots · compiled from live signals</div>
          </div>
          <div class="sf-brief" id="sf-brief-text"></div>
          <div class="sf-rule"></div>
          <div class="sf-rec" id="sf-rec">
            <div style="flex:1">
              <div class="sf-rec-label">Recommended activation</div>
              <div class="sf-rec-text" id="sf-rec-text"></div>
            </div>
            <button class="sf-rec-cta" id="sf-open-field" style="visibility:hidden">Open a Field →</button>
          </div>
        </div>
        <div class="sf-divider"></div>
        <div class="sf-right" id="sf-clusters"></div>
      </div>
      <div class="sf-bottom">
        <div class="sf-timestamp" id="sf-timestamp"></div>
        <div class="sf-refresh" id="sf-refresh">↻ Refresh</div>
      </div>
    `;
    this.bindStaticEvents();
  }

  private renderSkeleton() {
    if (!this.panel) return;
    this.panel.innerHTML = `
      <div class="sf-inner">
        <div class="sf-left">
          <div>
            <div class="sf-eyebrow">Intelligence brief</div>
            <div class="sf-attribution">Soho + Artists & Robots · reading the field…</div>
          </div>
          <div id="sf-brief-text" class="sf-brief">
            <div class="sf-skeleton sf-skeleton-line" style="width:95%"></div>
            <div class="sf-skeleton sf-skeleton-line" style="width:88%"></div>
            <div class="sf-skeleton sf-skeleton-line" style="width:72%"></div>
          </div>
          <div class="sf-rec" id="sf-rec">
            <div style="flex:1">
              <div class="sf-rec-label">Recommended activation</div>
              <div class="sf-rec-text" id="sf-rec-text">
                <div class="sf-skeleton sf-skeleton-line" style="width:90%;height:12px"></div>
                <div class="sf-skeleton sf-skeleton-line" style="width:70%;height:12px"></div>
              </div>
            </div>
            <button class="sf-rec-cta" id="sf-open-field" style="visibility:hidden">Open a Field →</button>
          </div>
        </div>
        <div class="sf-divider"></div>
        <div class="sf-right" id="sf-clusters">
          ${[1,2,3,4].map(() => `
            <div class="sf-skeleton-card">
              <div class="sf-skeleton sf-skeleton-card-title"></div>
              <div class="sf-skeleton sf-skeleton-card-text"></div>
              <div class="sf-skeleton sf-skeleton-card-text-short"></div>
            </div>`).join('')}
        </div>
      </div>
      <div class="sf-bottom">
        <div class="sf-timestamp" id="sf-timestamp">Generating intelligence brief…</div>
        <div class="sf-refresh" id="sf-refresh">↻ Refresh</div>
      </div>
    `;
    this.bindStaticEvents();
  }

  private clearSkeleton() {
    // Replace skeleton in brief area with empty text container
    const briefEl = document.getElementById('sf-brief-text');
    if (briefEl) briefEl.innerHTML = '';
    // Clear rec skeleton
    const recEl = document.getElementById('sf-rec-text');
    if (recEl) recEl.innerHTML = '';
  }

  private updateSkeletonMeta(clusterCount: number, signalCount: number) {
    const ts = document.getElementById('sf-timestamp');
    if (ts) ts.textContent = `${clusterCount} domains · ${signalCount} signals · generating…`;
    const attr = document.getElementById('sf-attribution');
    if (attr) attr.textContent = `Soho + Artists & Robots · ${clusterCount} domains · ${signalCount} signals`;
  }

  private updateBriefEl(final = false) {
    const el = document.getElementById('sf-brief-text');
    if (!el) return;
    if (final) {
      el.textContent = this.briefText;
    } else {
      el.innerHTML = this.escapeHtml(this.briefText) + '<span class="sf-cursor"></span>';
    }
  }

  private updateRecEl() {
    const el = document.getElementById('sf-rec-text');
    if (!el) return;
    el.innerHTML = this.escapeHtml(this.recText) + '<span class="sf-cursor"></span>';

    // Show CTA button once rec starts
    const cta = document.getElementById('sf-open-field') as HTMLButtonElement | null;
    if (cta) cta.style.visibility = 'visible';
  }

  private addOrUpdateClusterCard(topic: string) {
    const container = document.getElementById('sf-clusters');
    if (!container) return;

    // Remove skeleton placeholders once first card arrives
    if (container.querySelector('.sf-skeleton-card')) {
      container.innerHTML = '';
    }

    let card = document.getElementById(`sf-card-${this.cardId(topic)}`);
    if (!card) {
      const cl = this.clusters.get(topic)!;
      card = document.createElement('div');
      card.id = `sf-card-${this.cardId(topic)}`;
      card.className = 'sf-cluster-card';
      card.setAttribute('data-topic', topic);
      const trendSymbol = cl.trend === 'rising' ? '↑' : cl.trend === 'fading' ? '↓' : '–';
      const sigCount = cl.signalCount || 0;
      card.innerHTML = `
        <div class="sf-cluster-header">
          <div class="sf-cluster-name">${this.escapeHtml(topic)}</div>
          <div class="sf-cluster-data">
            <span class="sf-cluster-signals">${sigCount} sig</span>
            <span class="sf-cluster-trend ${cl.trend}">${trendSymbol}</span>
          </div>
        </div>
        <div class="sf-cluster-brief" id="sf-brief-${this.cardId(topic)}"></div>
      `;
      card.onclick = () => useAuraStore.getState().setMode('field');
      container.appendChild(card);

      // Stagger reveal — slight delay per card
      const delay = this.clusters.size * 80;
      setTimeout(() => card!.classList.add('revealed'), delay);
    }
  }

  private updateClusterCard(topic: string) {
    const briefEl = document.getElementById(`sf-brief-${this.cardId(topic)}`);
    if (!briefEl) return;
    const cl = this.clusters.get(topic)!;
    briefEl.innerHTML = this.escapeHtml(cl.brief) + '<span class="sf-cluster-cursor"></span>';
  }

  private finalize() {
    // Remove all cursors
    this.panel?.querySelectorAll('.sf-cursor, .sf-cluster-cursor').forEach(c => c.remove());

    // Final brief text — strip any markdown the model snuck in
    const briefEl = document.getElementById('sf-brief-text');
    if (briefEl) briefEl.textContent = this.stripMd(this.briefText);

    // Final rec text
    const recEl = document.getElementById('sf-rec-text');
    if (recEl) recEl.textContent = this.stripMd(this.recText);

    // Final cluster briefs
    this.clusters.forEach((cl, topic) => {
      const el = document.getElementById(`sf-brief-${this.cardId(topic)}`);
      if (el) el.textContent = cl.brief;
    });

    // Timestamp
    const ts = document.getElementById('sf-timestamp');
    if (ts && this.generatedAt) {
      const time = new Date(this.generatedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      ts.textContent = `Brief generated ${time}`;
    }
  }

  // ── Full render (from cache) ───────────────────────────────────────────────

  private renderFull() {
    this.renderEmpty();
    const briefEl = document.getElementById('sf-brief-text');
    if (briefEl) briefEl.textContent = this.briefText || 'Brief is loading…';

    const recEl = document.getElementById('sf-rec-text');
    if (recEl) recEl.textContent = this.recText;

    const cta = document.getElementById('sf-open-field') as HTMLButtonElement | null;
    if (cta && this.recText) cta.style.visibility = 'visible';

    const container = document.getElementById('sf-clusters');
    if (container) {
      container.innerHTML = '';
      this.clusters.forEach((cl, topic) => {
        const card = document.createElement('div');
        card.className = 'sf-cluster-card revealed';
        const trendSymbol = cl.trend === 'rising' ? '↑' : cl.trend === 'fading' ? '↓' : '–';
        card.innerHTML = `
          <div class="sf-cluster-header">
            <div class="sf-cluster-name">${this.escapeHtml(topic)}</div>
            <div class="sf-cluster-data">
              <span class="sf-cluster-signals">${cl.signalCount || 0} sig</span>
              <span class="sf-cluster-trend ${cl.trend}">${trendSymbol}</span>
            </div>
          </div>
          <div class="sf-cluster-brief">${this.escapeHtml(cl.brief)}</div>
        `;
        card.onclick = () => useAuraStore.getState().setMode('field');
        container.appendChild(card);
      });
    }

    if (this.generatedAt) {
      const ts = document.getElementById('sf-timestamp');
      const time = new Date(this.generatedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      if (ts) ts.textContent = `Brief generated ${time}`;
    }
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  private cardId(topic: string): string {
    return topic.toLowerCase().replace(/[^a-z0-9]+/g, '-');
  }

  private escapeHtml(text: string): string {
    return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  private stripMd(text: string): string {
    return text
      .replace(/\*\*([^*]+)\*\*/g, '$1')
      .replace(/\*([^*]+)\*/g, '$1')
      .replace(/^#{1,6}\s+/gm, '')
      .replace(/—/g, ',')
      .replace(/–/g, ',')
      .trim();
  }

  private bindStaticEvents() {
    document.getElementById('sf-open-field')?.addEventListener('click', () => {
      useAuraStore.getState().setMode('field');
    });
    const refreshEl = document.getElementById('sf-refresh');
    if (refreshEl) {
      refreshEl.addEventListener('click', () => {
        this.lastFetch = 0;
        this.startStream();
      });
    }
  }

  // ── update / dispose ──────────────────────────────────────────────────────

  update() {
    const mode = useAuraStore.getState().mode;
    const shouldShow = mode === 'sense';
    if (shouldShow && !this.visible) this.show();
    if (!shouldShow && this.visible) this.hide();
  }

  dispose() {
    this.hide();
    this.panel?.remove();
  }
}
